import { Page } from 'Common/Page/Page'
import { PageHeader } from 'Common/Supplyvue-ui'
import { Box, Typography, Card, CardContent, Tabs, Tab } from '@mui/material'
import { useScenarioComparison } from 'Features/Projects/Analysis/ScenarioComparison.data'
import { useParams } from 'react-router-dom'
// import { ScenarioComparisonTable } from 'Features/Projects/Analysis/ScenarioComparison.table'
import { Button } from 'Common/Supplyvue-ui'
import { useState, useMemo } from 'react'
import { BaseTable } from 'Features/Projects/Table/BaseTable.view'
import { GridColDef } from '@mui/x-data-grid-premium'

const sx = {
  '& .MuiDataGrid-root': {
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    backgroundColor: '#ffffff',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#f5f5f5',
    fontWeight: 'bold',
    borderBottom: '2px solid #e0e0e0',
  },
  '& .MuiDataGrid-row:nth-of-type(odd)': {
    backgroundColor: '#fafafa',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: '#f0f0f0',
  },
  '& .font-weight-bold': {
    fontWeight: '600',
  },
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid #e8e8e8',
  },
  '& .MuiDataGrid-columnSeparator': {
    color: '#e0e0e0',
  },
}

const tradeOffMetricsMapping = {
  weighted_service_level: 'Weighted Service Level',
  production_efficiency: 'Production Efficiency',
  bau_stock_duration: 'BAU Stock Duration',
}

const efficiencyMetricsMapping = {
  available_capacity_hours: 'Available Capacity',
  avg_utilised_time: 'Capacity Usage',
  avg_utilisation: 'Utilisation',
  avg_run_rate: 'Run Rate',
  avg_changeover_time: 'Changeover Time',
  pct_changeover_time: '%Changeover Time',
  avg_sku_changeovers_per_week: 'SKU Changeovers',
}

const cycleSpeedMetricsMapping = {
  weekly_throughput: 'Weekly Throughput',
  vol_weighted_cycle_time: 'Volume Weighted Cycle Time',
  avg_cycle_time: 'Average Cycle Time',
  foc_count: 'FOC Count',
  foq_count: 'FOQ Count',
  pct_foc_volume: 'PCT FOC Volume',
  pct_cycle_1_volume: 'PCT Cycle 1 Volume',
}

const inventoryMetricsMapping = {
  avg_stock_amount: 'Cycle Stock Amount',
  safety_stock_amount: 'Safety Stock Amount',
  avg_stock_duration: 'Cycle Stock Duration',
  daily_sales_rate: 'Daily Sales Rate',
  safety_stock_duration: 'Safety Stock Duration',
  coupled_count: 'Coupled Count',
  pct_coupled_count: 'PCT Coupled Count',
}

const assumptionMetricsMapping = {
  sales_rate_period_type: 'Sales Rate Period Type',
  period_start_week: 'Period Start Week',
  number_of_weeks: 'Number of Weeks',
}

type ScenarioComparisonParams = {
  projectId: string
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

export const ScenarioComparisonView = () => {
  const { projectId } = useParams<ScenarioComparisonParams>() as {
    projectId: string
  }
  const [tabValue, setTabValue] = useState(0)

  const { data, isLoading } = useScenarioComparison(projectId)

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  const handleExportData = () => {
    // TODO: Implement export functionality
    console.log('Export data clicked')
  }

  // Mock data for demonstration - replace with actual data from API

  const tradeOffData = useMemo(() => {
    if (!data) return []
    const tradeOffMetrics = Object.keys(tradeOffMetricsMapping)
    return data.filter((item) =>
      tradeOffMetrics.includes(item.Metric as string)
    )
  }, [data])

  const efficiencyData = useMemo(() => {
    if (!data) return []
    const efficiencyMetrics = Object.keys(efficiencyMetricsMapping)
    return data.filter((item) =>
      efficiencyMetrics.includes(item.Metric as string)
    )
  }, [data])

  const cycleSpeedData = useMemo(() => {
    if (!data) return []
    const cycleSpeedMetrics = Object.keys(cycleSpeedMetricsMapping)
    return data.filter((item) =>
      cycleSpeedMetrics.includes(item.Metric as string)
    )
  }, [data])

  const inventoryData = useMemo(() => {
    if (!data) return []
    const inventoryMetrics = Object.keys(inventoryMetricsMapping)
    return data.filter((item) =>
      inventoryMetrics.includes(item.Metric as string)
    )
  }, [data])

  const assumptionData = useMemo(() => {
    if (!data) return []
    const assumptionMetrics = Object.keys(assumptionMetricsMapping)
    return data.filter((item) =>
      assumptionMetrics.includes(item.Metric as string)
    )
  }, [data])

  const availableScenarios = useMemo(() => {
    const keysToExclude = ['Metric', 'id', 'Category']
    if (!data) return []
    const scenarios = new Set<string>()
    data.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (!keysToExclude.includes(key)) {
          scenarios.add(key)
        }
      })
    })
    return Array.from(scenarios)
  }, [data])

  console.log('Available Scenarios:', availableScenarios)

  // Column definitions for BaseTable
  const tradeOffColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'Metric',
        headerName: 'Metric',
        width: 200,
        editable: false,
        cellClassName: 'font-weight-bold',
        valueGetter: (value) => {
          return tradeOffMetricsMapping[value] || value
        },
      },
      ...availableScenarios.map((scenario) => ({
        field: scenario,
        headerName: scenario,
        width: 150,
        editable: false,
        headerAlign: 'center',
        align: 'center',
      })),
    ],
    [availableScenarios]
  )

  const efficiencyColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'Metric',
        headerName: 'Metric',
        width: 200,
        editable: false,
        cellClassName: 'font-weight-bold',
        valueGetter: (value) => {
          return efficiencyMetricsMapping[value] || value
        },
      },
      ...availableScenarios.map((scenario) => ({
        field: scenario,
        headerName: scenario,
        width: 150,
        editable: false,
        headerAlign: 'center',
        align: 'center',
      })),
    ],
    [availableScenarios]
  )

  const cycleSpeedColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'Metric',
        headerName: 'Metric',
        width: 200,
        editable: false,
        cellClassName: 'font-weight-bold',
        valueGetter: (value) => {
          return cycleSpeedMetricsMapping[value] || value
        },
      },
      ...availableScenarios.map((scenario) => ({
        field: scenario,
        headerName: scenario,
        width: 150,
        editable: false,
        headerAlign: 'center',
        align: 'center',
      })),
    ],
    [availableScenarios]
  )

  const inventoryColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'Metric',
        headerName: 'Metric',
        width: 200,
        editable: false,
        cellClassName: 'font-weight-bold',
        valueGetter: (value) => {
          return inventoryMetricsMapping[value] || value
        },
      },
      ...availableScenarios.map((scenario) => ({
        field: scenario,
        headerName: scenario,
        width: 150,
        editable: false,
        headerAlign: 'center',
        align: 'center',
      })),
    ],
    [availableScenarios]
  )

  const assumptionColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'Metric',
        headerName: 'Metric',
        width: 200,
        editable: false,
        cellClassName: 'font-weight-bold',
        valueGetter: (value) => {
          return assumptionMetricsMapping[value] || value
        },
      },
      ...availableScenarios.map((scenario) => ({
        field: scenario,
        headerName: scenario,
        width: 150,
        editable: false,
        headerAlign: 'center',
        align: 'center',
      })),
    ],
    [availableScenarios]
  )

  // Dummy functions for BaseTable required props
  const handleUpdateTableData = () => {
    console.log('Update table data - not needed for read-only')
  }

  const handleUploadData = () => {
    console.log('Upload data - not needed for read-only')
  }

  return (
    <Page>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        mt={2}
        width="100%"
      >
        <PageHeader
          title="Scenario Comparison Dashboard"
          rightElement={
            <Button variant="contained" onClick={handleExportData}>
              Export
            </Button>
          }
        />

        {/* Trade-offs Summary Card */}
        <Card sx={{ backgroundColor: '#f8f9fa' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Scenario Trade-offs
            </Typography>
            <Box sx={{ height: 280 }}>
              <BaseTable
                data={tradeOffData}
                isLoading={isLoading}
                updateTableData={handleUpdateTableData}
                uploadData={handleUploadData}
                isUploading={false}
                isSaving={false}
                checkBoxSelection={false}
                showTableToolBar={false}
                columns={tradeOffColumns}
                downloadFileName="scenario-tradeoffs"
                sx={{
                  '& .MuiDataGrid-root': {
                    border: 'none',
                    backgroundColor: 'transparent',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f5f5f5',
                    fontWeight: 'bold',
                  },
                  '& .MuiDataGrid-row:nth-of-type(odd)': {
                    backgroundColor: '#fafafa',
                  },
                  '& .font-weight-bold': {
                    fontWeight: '600',
                  },
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid #e0e0e0',
                  },
                }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Tab Navigation */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="scenario comparison tabs"
          >
            <Tab label="Efficiency" />
            <Tab label="Cycle speed" />
            <Tab label="Inventory" />
            <Tab label="Assumptions" />
          </Tabs>
        </Box>
        <Box>
          {/* Operational Tab Content */}
          <TabPanel value={tabValue} index={0}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Operational Efficiency Metrics
            </Typography>
            <Box sx={{ height: 400 }}>
              <BaseTable
                data={efficiencyData}
                isLoading={isLoading}
                updateTableData={handleUpdateTableData}
                uploadData={handleUploadData}
                isUploading={false}
                isSaving={false}
                checkBoxSelection={false}
                showTableToolBar={false}
                columns={efficiencyColumns}
                downloadFileName="operational-metrics"
                // sx={sx}
              />
            </Box>
          </TabPanel>

          {/* Working Capital Tab Content */}
          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Cycle speed
            </Typography>
            {/* <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Working capital analysis will be displayed here.
          </Typography> */}
            <Box sx={{ height: 400 }}>
              <BaseTable
                data={cycleSpeedData}
                isLoading={isLoading}
                updateTableData={handleUpdateTableData}
                uploadData={handleUploadData}
                isUploading={false}
                isSaving={false}
                checkBoxSelection={false}
                showTableToolBar={false}
                columns={cycleSpeedColumns}
                downloadFileName="operational-metrics"
                sx={sx}
              />
            </Box>
          </TabPanel>
          {/* Inventory Tab Content */}
          <TabPanel value={tabValue} index={2}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Inventory
            </Typography>
            <Box sx={{ height: 400 }}>
              <BaseTable
                data={inventoryData}
                isLoading={isLoading}
                updateTableData={handleUpdateTableData}
                uploadData={handleUploadData}
                isUploading={false}
                isSaving={false}
                checkBoxSelection={false}
                showTableToolBar={false}
                columns={inventoryColumns}
                downloadFileName="operational-metrics"
                sx={sx}
              />
            </Box>
          </TabPanel>

          {/* Assumptions Tab Content */}
          <TabPanel value={tabValue} index={3}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Assumptions
            </Typography>
            <Box sx={{ height: 400 }}>
              <BaseTable
                data={assumptionData}
                isLoading={isLoading}
                updateTableData={handleUpdateTableData}
                uploadData={handleUploadData}
                isUploading={false}
                isSaving={false}
                checkBoxSelection={false}
                showTableToolBar={false}
                columns={assumptionColumns}
                downloadFileName="operational-metrics"
                sx={sx}
              />
            </Box>
          </TabPanel>
        </Box>

        {/* Commented out old table */}
        {/* {data && <ScenarioComparisonTable data={data} isLoading={isLoading} />} */}
      </Box>
    </Page>
  )
}
